// /apps/patient/src/pages/programs/PersonalizePlanPage.tsx
// Collects the Fitness answers that static-routine arrivals skipped at onboarding, saves them to
// patient.fitness_profile, then builds a personalized plan. Reached from "Switch to a personalized plan".

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { usePatient } from '../../contexts/PatientContext';
import { patientApiService } from '../../services/api';
import { FitnessProfileStep, buildFitnessProfile } from '../PatientOnboarding';

export function PersonalizePlanPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { patient: userProfile, refreshPatient } = usePatient();
  const patientData = userProfile?.patient;

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [building, setBuilding] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Prefill from whatever the profile already holds (empty for routine arrivals).
  useEffect(() => {
    if (!patientData || prefilled) return;
    const fp = patientData.fitness_profile || {};
    setFormData({
      fitness_goals: fp.goals || [],
      fitness_level: fp.fitness_level || '',
      physical_condition: fp.physical_condition,
      preferred_duration: fp.preferred_duration || 30,
      available_equipment: fp.available_equipment || [],
      physical_limitations: fp.physical_limitations,
      cardio_preferred_activities: fp.cardio_preferences?.preferred_activities || [],
      outdoor_preference: fp.cardio_preferences?.outdoor_preference,
      preferred_workout_types: fp.training_preferences?.preferred_workout_types || [],
      preferred_days: fp.training_preferences?.preferred_days || [],
      preferred_time_of_day: fp.training_preferences?.preferred_time_of_day,
      sessions_per_week: fp.training_preferences?.sessions_per_week || 3,
    });
    setPrefilled(true);
  }, [patientData, prefilled]);

  const updateFormData = (updates: any) => setFormData((prev: any) => ({ ...prev, ...updates }));

  const handleBuild = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fitness_goals?.length) newErrors.fitness_goals = 'Please select at least one goal';
    if (!formData.fitness_level) newErrors.fitness_level = 'Please select your fitness level';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 || !patientData?.id) return;

    setBuilding(true);
    try {
      await patientApiService.updatePatientRecord(patientData.id, {
        fitness_profile: buildFitnessProfile(formData, patientData.fitness_profile, { exercisePlatform: true, includeHealthScreening: false }),
      });
      await refreshPatient();
      const result = await patientApiService.generatePlan();
      if (result?.assignment_id) {
        navigate(`/programs/${result.assignment_id}`, { replace: true });
        return;
      }
      navigate('/programs');
    } catch (error: any) {
      console.error('Personalize failed:', error);
      setErrors({ submit: error?.message || 'Your answers are saved, but we could not build your plan. Tap Build my plan to try again.' });
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <PageHeader title="Tell us about you" showBack />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-gray-600 mb-6">
          A few answers and we will build a plan around your goals, your equipment and your week. Your current routine stays available until the new plan is ready.
        </p>

        <div className="bg-white rounded-lg shadow-sm p-6">
          {prefilled && (
            <FitnessProfileStep
              formData={formData}
              updateFormData={updateFormData}
              errors={errors}
              isExercisePlatform
            />
          )}
        </div>

        <div className="mt-6 flex flex-col items-end gap-2">
          {errors.submit && (
            <p className="text-sm text-red-600 text-right max-w-md">{errors.submit}</p>
          )}
          <button
            onClick={handleBuild}
            disabled={building}
            className="bg-primary text-white px-6 py-3 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {building ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Building your plan...
              </>
            ) : (
              'Build my plan'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
